import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { TrendingUp, Target, Trophy, Sparkles, Play, Flame, ChevronRight, Clipboard, Lightbulb } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const LEVELS = [
  { name: "Anfänger", min: 0, max: 100 },
  { name: "Junior Seller", min: 100, max: 300 },
  { name: "Senior Seller", min: 300, max: 700 },
  { name: "Elite Seller", min: 700, max: 1500 },
  { name: "Novatus Master", min: 1500, max: 9999 },
];

const ScoreRing = ({ score = 0, size = 120 }) => {
  const radius = (size - 16) / 2;
  const c = 2 * Math.PI * radius;
  const offset = c - (score / 100) * c;
  const color = score >= 85 ? "#1FCB4F" : score >= 70 ? "#1FCB4F" : score >= 50 ? "#FBBF24" : "#737373";
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size/2} cy={size/2} r={radius} stroke="#262626" strokeWidth="8" fill="none" />
        <circle cx={size/2} cy={size/2} r={radius} stroke={color} strokeWidth="8" fill="none"
          strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 1.2s ease-out" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-display font-black text-3xl text-white">{score}</span>
        <span className="text-[10px] uppercase tracking-[0.15em] text-novatus-text-3 font-semibold">Ø Score</span>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [mandatoryScenarios, setMandatoryScenarios] = useState([]);
  const [scenarioStats, setScenarioStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [insightsStatus, setInsightsStatus] = useState(null);

  const isAdmin = ["admin", "trainer", "manager", "direktor"].includes(user?.role);

  useEffect(() => {
    (async () => {
      try {
        const [p, a, s, st] = await Promise.all([
          api.get("/me/progress"),
          api.get("/me/assignments"),
          api.get("/scenarios"),
          api.get("/me/scenario-stats"),
        ]);
        setProgress(p.data);
        setAssignments(a.data || []);
        setMandatoryScenarios(
          (s.data || [])
            .filter(x => x.is_mandatory === true)
            .sort((x, y) => (x.order || 99) - (y.order || 99))
        );
        setScenarioStats(st.data || {});
        // Admin-only: load insights status for the dashboard hint
        if (isAdmin) {
          try {
            const ins = await api.get("/admin/insights/status");
            setInsightsStatus(ins.data);
          } catch (e) { /* non-critical */ }
        }
      } catch (e) { console.warn("Dashboard load failed", e); }
      finally { setLoading(false); }
    })();
  }, [isAdmin]);

  if (loading) return <div className="p-12 text-center text-novatus-text-3">Lade...</div>;

  const level = progress?.level || "Anfänger";
  const levelInfo = LEVELS.find(l => l.name === level) || LEVELS[0];
  const xp = progress?.xp || 0;
  const xpInLevel = xp - levelInfo.min;
  const xpForLevel = levelInfo.max - levelInfo.min;
  const xpPct = Math.min(100, Math.round((xpInLevel / xpForLevel) * 100));
  const openAssignments = assignments.filter(a => a.status === "open");

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
      <div className="animate-slide-up">
        <span className="label-eyebrow">Willkommen zurück</span>
        <h1 className="font-display font-black text-3xl sm:text-5xl tracking-tight text-white mt-2">
          Hallo, {user?.first_name}.
        </h1>
        <p className="text-novatus-text-2 mt-3 max-w-2xl">
          Bereit für dein nächstes <span className="text-novatus-green font-semibold">KI-Sparring</span>? Wähle ein Szenario und übe mit deinem KI-Kunden.
        </p>
      </div>

      {/* Admin-only: notification when AI has new insight suggestions ready */}
      {isAdmin && insightsStatus?.pending > 0 && (
        <Link
          to="/admin"
          onClick={() => {
            // The Admin page reads tab from internal state — sellers will land on default.
            // We tee up a session key so Admin can auto-switch to insights tab if needed.
            try { sessionStorage.setItem("admin_open_tab", "insights"); } catch (e) { /* noop */ }
          }}
          className="block mt-6 nv-card p-4 sm:p-5 border-novatus-green/40 hover:border-novatus-green/70 transition-colors animate-slide-up"
          data-testid="insights-hint-card"
        >
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-novatus-green/10 border border-novatus-green/40 flex items-center justify-center shrink-0">
              <Lightbulb className="w-5 h-5 text-novatus-green" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-display font-bold text-white">
                {insightsStatus.pending} neue{insightsStatus.pending === 1 ? "r" : ""} KI-Insight{insightsStatus.pending === 1 ? "" : "s"} bereit zur Freigabe
              </p>
              <p className="text-xs text-novatus-text-3 mt-0.5">
                Die KI hat aus den letzten Trainings konkrete Verbesserungen vorgeschlagen. Jetzt prüfen & übernehmen.
              </p>
            </div>
            <ChevronRight className="w-5 h-5 text-novatus-green shrink-0" />
          </div>
        </Link>
      )}

      {/* Hero CTA */}
      <div className="nv-card mt-8 overflow-hidden relative animate-slide-up delay-1" data-testid="start-training-card">
        <div className="absolute inset-0 opacity-30 pointer-events-none" style={{
          background: 'radial-gradient(circle at 85% 50%, rgba(31,203,79,0.35) 0%, transparent 60%)',
        }} />
        <div className="relative p-6 sm:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="max-w-xl">
            <div className="flex items-center gap-2 mb-3">
              <Flame className="w-4 h-4 text-novatus-green" />
              <span className="text-[11px] uppercase tracking-[0.28em] font-semibold text-novatus-green">Heute trainieren</span>
            </div>
            <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tight text-white leading-tight">
              Starte dein nächstes KI-Sparring.
            </h2>
            <p className="text-novatus-text-2 mt-3 text-sm leading-relaxed">
              3 Pflicht-Lektionen plus 8 freie Szenarien — vom Erstkontakt bis zur Preisdiskussion und Rückgewinnung.
            </p>
          </div>
          <button
            onClick={() => navigate("/training")}
            className="btn-green h-12 px-6 rounded-xl flex items-center gap-2"
            data-testid="start-training-btn"
          >
            <Play className="w-4 h-4" fill="currentColor" />
            Training starten
          </button>
        </div>
      </div>

      {/* Pflicht-Programm — visible for EVERY user, regardless of trainer assignments */}
      {mandatoryScenarios.length > 0 && (
        <div className="mt-6 nv-card p-5 sm:p-6 border-novatus-green/40 animate-slide-up delay-1" data-testid="mandatory-program">
          <div className="flex items-baseline gap-2 flex-wrap">
            <Clipboard className="w-4 h-4 text-novatus-green self-center" />
            <span className="text-[11px] uppercase tracking-[0.25em] font-bold text-novatus-green">Pflicht-Programm</span>
            <span className="text-xs text-novatus-text-3">
              {mandatoryScenarios.length} Lektionen · leicht → mittel → schwer · für alle Verkäufer
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
            {mandatoryScenarios.map((s, i) => {
              const stat = scenarioStats[s.id];
              const num = ["I","II","III","IV","V"][((s.order || i+1) - 1)] || (s.order || i+1);
              const colorRing = s.difficulty === "leicht" ? "bg-novatus-green" : s.difficulty === "mittel" ? "bg-amber-500" : "bg-red-500";
              return (
                <button
                  key={s.id}
                  onClick={() => navigate("/training")}
                  className="text-left rounded-xl bg-novatus-card-2 border border-novatus hover:border-novatus-green/60 hover:bg-novatus-green/5 p-4 transition-colors group"
                  data-testid={`mandatory-card-${s.order || i+1}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-novatus-green text-black">
                      Lektion {num}
                    </span>
                    <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full diff-${s.difficulty}`}>
                      {s.difficulty}
                    </span>
                  </div>
                  <p className="font-display font-bold text-sm text-white mt-3 leading-snug line-clamp-2">
                    {s.title.replace(/^Lektion [IVX]+\s*—\s*/, "")}
                  </p>
                  <div className="mt-3 flex items-center justify-between text-[11px]">
                    {stat && stat.count > 0 ? (
                      <span className="text-novatus-text-2">
                        <span className="font-semibold text-white">{stat.count}×</span> · Ø <span className="font-semibold text-white">{stat.avg_score}</span>
                      </span>
                    ) : (
                      <span className="text-amber-400 font-semibold">Noch nicht trainiert</span>
                    )}
                    <span className="text-novatus-green opacity-0 group-hover:opacity-100 transition-opacity">Trainieren →</span>
                  </div>
                  <div className={`h-1 rounded-full ${colorRing} mt-3`} />
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Open assignments banner */}
      {openAssignments.length > 0 && (
        <div className="mt-5 nv-card p-5 border-novatus-green/40 animate-slide-up delay-1" data-testid="assignments-banner">
          <div className="flex items-start gap-3">
            <Clipboard className="w-5 h-5 text-novatus-green mt-0.5" />
            <div className="flex-1">
              <p className="text-[11px] uppercase tracking-[0.25em] font-bold text-novatus-green">Trainer-Zuweisungen</p>
              <p className="text-white mt-1">
                <span className="font-semibold">{openAssignments.length}</span> offene{openAssignments.length === 1 ? "s" : ""} Pflicht-Training: <span className="text-novatus-green">{openAssignments[0].scenario_title}</span>
              </p>
              {openAssignments[0].note && <p className="text-sm text-novatus-text-2 mt-1">"{openAssignments[0].note}" — {openAssignments[0].assigned_by_name}</p>}
            </div>
            <button onClick={() => navigate("/training")} className="btn-ghost-green h-9 px-4 rounded-lg text-sm font-semibold" data-testid="assignment-go-btn">
              Jetzt erledigen
            </button>
          </div>
        </div>
      )}

      {/* Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
        <div className="nv-card nv-card-hover p-6 animate-slide-up delay-2" data-testid="metric-score">
          <div className="flex items-start justify-between">
            <div>
              <p className="label-eyebrow">Ø Score</p>
              <p className="font-display font-black text-4xl text-white mt-2">{progress?.average_score || 0}</p>
              <p className="text-xs text-novatus-text-3 mt-1">über {progress?.total_sessions || 0} Trainings</p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-novatus-green/10 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-novatus-green" />
            </div>
          </div>
        </div>

        <div className="nv-card nv-card-hover p-6 animate-slide-up delay-2" data-testid="metric-level">
          <div className="flex items-start justify-between">
            <div>
              <p className="label-eyebrow">Level</p>
              <p className="font-display font-black text-2xl text-white mt-2 leading-tight">{level}</p>
              <p className="text-xs text-novatus-text-3 mt-1">{xp} XP gesammelt</p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-novatus-green/10 flex items-center justify-center">
              <Trophy className="w-5 h-5 text-novatus-green" />
            </div>
          </div>
        </div>

        <div className="nv-card nv-card-hover p-6 animate-slide-up delay-3" data-testid="metric-progress">
          <p className="label-eyebrow">Nächstes Level</p>
          <div className="mt-3 flex items-end justify-between">
            <span className="font-display font-black text-2xl text-white">{xpPct}%</span>
            <span className="text-xs text-novatus-text-3">{xpInLevel}/{xpForLevel} XP</span>
          </div>
          <div className="mt-3 h-2 bg-novatus-card-2 rounded-full overflow-hidden">
            <div className="h-full bg-novatus-green rounded-full animate-fill" style={{ width: `${xpPct}%` }} />
          </div>
        </div>

        <div className="nv-card nv-card-hover p-6 animate-slide-up delay-3" data-testid="metric-daily-goal">
          <div className="flex items-start justify-between">
            <div>
              <p className="label-eyebrow">Tagesziel</p>
              <p className="font-display font-black text-4xl text-white mt-2">
                {progress?.daily_goal_done || 0}<span className="text-2xl text-novatus-text-3">/{progress?.daily_goal_target || 1}</span>
              </p>
              <p className="text-xs text-novatus-text-3 mt-1">Trainings heute</p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-novatus-green/10 flex items-center justify-center">
              <Target className="w-5 h-5 text-novatus-green" />
            </div>
          </div>
        </div>
      </div>

      {/* Score ring + recommendation */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <div className="nv-card p-7 animate-slide-up delay-4 flex items-center gap-6" data-testid="score-ring-card">
          <ScoreRing score={progress?.average_score || 0} />
          <div>
            <p className="label-eyebrow">Performance</p>
            <p className="font-display font-bold text-xl text-white mt-2">{progress?.total_sessions || 0} Trainings</p>
            <p className="text-sm text-novatus-text-2 mt-2 leading-relaxed">
              {progress?.average_score >= 70 ? "Starke Quote — bleib dran und gehe an den Abschluss." :
               progress?.average_score >= 50 ? "Solide. Übe gezielt Einwandbehandlung." :
               "Trainiere täglich — Wiederholung schlägt Talent."}
            </p>
          </div>
        </div>

        <div className="lg:col-span-2 nv-card p-7 animate-slide-up delay-4" data-testid="recommendation-card">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-novatus-green" />
            <span className="label-eyebrow">Letzte KI-Empfehlung</span>
          </div>
          {progress?.last_recommendation ? (
            <>
              <h3 className="font-display font-bold text-2xl text-white">{progress.last_recommendation}</h3>
              <p className="text-sm text-novatus-text-2 mt-2 leading-relaxed">
                Basierend auf deinem letzten Feedback empfehlen wir dieses Training als Nächstes.
              </p>
              <button onClick={() => navigate("/training")} className="btn-ghost-green mt-4 h-10 px-4 rounded-lg text-sm flex items-center gap-1" data-testid="goto-recommendation-btn">
                Jetzt ausprobieren <ChevronRight className="w-4 h-4" />
              </button>
            </>
          ) : (
            <>
              <h3 className="font-display font-bold text-2xl text-white">Erstes Training starten</h3>
              <p className="text-sm text-novatus-text-2 mt-2 leading-relaxed">
                Sobald du dein erstes Training absolviert hast, schlägt dir die KI das nächste passende Szenario vor.
              </p>
              <button onClick={() => navigate("/training")} className="btn-green mt-4 h-10 px-5 rounded-lg flex items-center gap-1" data-testid="goto-training-btn">
                Loslegen <ChevronRight className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Recent */}
      <div className="mt-10">
        <h2 className="font-display font-bold text-2xl text-white">Letzte Trainings</h2>
        <div className="mt-4 grid gap-3">
          {(progress?.recent_sessions || []).length === 0 ? (
            <p className="text-sm text-novatus-text-3">Noch keine abgeschlossenen Trainings.</p>
          ) : progress.recent_sessions.slice(0, 5).map((s, idx) => (
            <div key={s.id || idx} className="nv-card nv-card-hover p-4 flex items-center justify-between" data-testid={`recent-session-${idx}`}>
              <div>
                <p className="font-semibold text-white">{s.scenario_title}</p>
                <p className="text-xs text-novatus-text-3 mt-0.5">{new Date(s.ended_at).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" })}</p>
              </div>
              <div className="flex items-center gap-4">
                <span className="px-2.5 py-1 rounded-md bg-novatus-card-2 border border-novatus text-xs font-mono text-novatus-green">+{s.xp_earned} XP</span>
                <div className="font-display font-black text-2xl text-white">{s.score}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
